/**
 * `useResize` hook for listening to resize and scroll events on a specific
 * DOM element and browser window.
 */

import * as React from "react"
import ResizeObserver from "resize-observer-polyfill"
import useDebouncedCallback from "../useDebouncedCallback"

/**
 * @param element DOM element to track for resize and scroll events
 * @param onChange Event handler for resize and scroll events
 * @param delay Debounce delay (milliseconds) for triggering onChange handler
 * @param immediatelyInvoke call onChange immediately when useResize is invoked
 */
const useResize = (
  element: Element | null,
  onChange: () => void,
  delay = 0,
  immediatelyInvoke = true
) => {
  const debouncedCallback = useDebouncedCallback(onChange, delay)
  const changeHandler = delay > 0 ? debouncedCallback : onChange

  React.useLayoutEffect(() => {
    immediatelyInvoke && onChange()
    const resizeObserver = new ResizeObserver(changeHandler)

    if (element !== null && element !== undefined) {
      resizeObserver.observe(element)
      element.addEventListener("scroll", changeHandler)
    }

    return () => {
      if (element !== null && element !== undefined) {
        resizeObserver.unobserve(element)
        element.removeEventListener("scroll", changeHandler)
      }
    }
  }, [changeHandler, element, immediatelyInvoke, onChange])
}

export default useResize
